// Weatherford Special Rate Packages — isolated dispatch pricing block rendered
// inside the TAXI Calculator. Dispatchers pick a named package (or let the
// geo-fence auto-detect one from a Weatherford pickup + destination); the
// per-rider rate, booking fee and minimum-rider count lock automatically,
// add-ons + the 2%-on-eligible-cards surcharge compute server-side. Never
// touches booking data.
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Calculator, Copy, Printer, Lock, Info, Wifi, Baby, CreditCard,
  MapPin, CheckCircle2, ShieldAlert, Navigation,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const usd = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n));

// Payment types → whether they carry the 2% surcharge (mirrors the backend rule).
const PAYMENT_TYPES = [
  { key: "cash", label: "Cash", fee: false },
  { key: "visa", label: "Visa", fee: false },
  { key: "cashapp", label: "Cash App", fee: false },
  { key: "venmo", label: "Venmo", fee: false },
  { key: "mastercard", label: "Mastercard (+2%)", fee: true },
  { key: "discover", label: "Discover (+2%)", fee: true },
  { key: "amex", label: "Amex (+2%)", fee: true },
];

export default function SpecialRateCalculator({ config }) {
  const specials = useMemo(
    () => (config?.special_rates || []).filter((s) => s.enabled !== false),
    [config]
  );
  const addons = config?.addons || {};

  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  const [geo, setGeo] = useState(null);        // { both_inside, suggested_package_key, ... }
  const [checking, setChecking] = useState(false);

  const [packageKey, setPackageKey] = useState("");
  const [riders, setRiders] = useState("");
  const [childSeats, setChildSeats] = useState("0");
  const [satcom, setSatcom] = useState(false);
  const [paymentType, setPaymentType] = useState("cash");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Track how the current selection was made so the geo-fence never clobbers
  // a dispatcher's manual override.
  const [autoApplied, setAutoApplied] = useState(false);
  const manualRef = useRef(false);
  const autoRef = useRef(false);

  const pkg = useMemo(() => specials.find((s) => s.key === packageKey) || null, [specials, packageKey]);

  const applyPackage = (key, auto) => {
    setPackageKey(key);
    const p = specials.find((s) => s.key === key);
    if (p) setRiders(String(p.min_riders || 1));
    if (auto) {
      autoRef.current = true; setAutoApplied(true);
    } else {
      manualRef.current = true; autoRef.current = false; setAutoApplied(false);
    }
  };

  const onPickPackage = (key) => applyPackage(key, false);

  // --- Geo-fence auto-detection (debounced on both addresses) ---------------
  useEffect(() => {
    const p = pickup.trim();
    const d = destination.trim();
    if (p.length < 5 || d.length < 5) { setGeo(null); return undefined; }
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await adminApi.post("/taxi-calculator/geofence-check", { pickup: p, destination: d });
        if (cancelled) return;
        setGeo(data);
        if (data.both_inside && data.suggested_package_key && !manualRef.current) {
          applyPackage(data.suggested_package_key, true);
        } else if (!data.both_inside && autoRef.current && !manualRef.current) {
          // was auto-applied, now no longer both-inside → clear it
          setPackageKey(""); setResult(null); autoRef.current = false; setAutoApplied(false);
        }
      } catch {
        if (!cancelled) setGeo(null);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 700);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, destination]);

  // --- Live recompute: total updates immediately on any change --------------
  const runQuote = async (silent) => {
    if (!packageKey) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await adminApi.post("/taxi-calculator/special-quote", {
        package_key: packageKey,
        riders: Math.max(1, parseInt(riders || "1", 10) || 1),
        child_seats: Math.max(0, parseInt(childSeats || "0", 10) || 0),
        satcom_wifi: satcom,
        payment_type: paymentType,
      });
      setResult(data);
      if (!silent && data.riders_capped) toast.info(`Capped at ${data.max_riders} riders for this package`);
    } catch (e) {
      if (!silent) { toast.error(e?.response?.data?.detail || "Calculation failed."); setResult(null); }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!packageKey) { setResult(null); return undefined; }
    const t = setTimeout(() => { runQuote(true); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageKey, riders, childSeats, satcom, paymentType]);

  const buildBreakdownText = () => {
    if (!result) return "";
    const lines = [
      "Buller Taxi — Weatherford Special Rate",
      `Package: ${result.package_display}`,
      pickup.trim() ? `Pickup: ${pickup.trim()}` : null,
      destination.trim() ? `Destination: ${destination.trim()}` : null,
      `Per-rider: ${usd(result.per_rider)} × ${result.riders} riders = ${usd(result.rider_subtotal)}`,
      `Booking fee: ${usd(result.booking_fee)}`,
      `Package fare: ${usd(result.package_fare)}`,
    ].filter(Boolean);
    if (result.child_seats > 0)
      lines.push(`Child safety seats: ${usd(result.child_seat_rate)} × ${result.child_seats} = ${usd(result.child_seat_amount)}`);
    if (result.satcom_wifi)
      lines.push(`Satcom Wi-Fi: ${result.satcom_free ? "FREE (package ≥ $200)" : `${usd(result.satcom_wifi_rate)} × ${result.riders} = ${usd(result.satcom_amount)}`}`);
    lines.push(`Subtotal: ${usd(result.subtotal)}`);
    if (result.cc_surcharge_applies)
      lines.push(`Card surcharge (${(Number(result.cc_surcharge_pct) * 100).toFixed(0)}% · ${result.payment_type}): ${usd(result.cc_surcharge_amount)}`);
    lines.push(`TOTAL: ${usd(result.total)}`);
    if (result.note) lines.push(`Note: ${result.note}`);
    return lines.join("\n");
  };

  const copyBreakdown = async () => {
    try {
      await navigator.clipboard.writeText(buildBreakdownText());
      toast.success("Special rate breakdown copied");
    } catch {
      toast.error("Copy failed — clipboard unavailable");
    }
  };

  const printQuote = () => {
    if (!result) return;
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const rows = [
      ["Per-rider", `${usd(result.per_rider)} × ${result.riders}`, usd(result.rider_subtotal)],
      ["Booking fee (once)", "", usd(result.booking_fee)],
      result.child_seats > 0 ? ["Child safety seats", `${usd(result.child_seat_rate)} × ${result.child_seats}`, usd(result.child_seat_amount)] : null,
      result.satcom_wifi ? ["Satcom Wi-Fi", result.satcom_free ? "FREE (≥ $200)" : `${usd(result.satcom_wifi_rate)} × ${result.riders}`, usd(result.satcom_amount)] : null,
      result.cc_surcharge_applies ? [`Card surcharge (${(Number(result.cc_surcharge_pct) * 100).toFixed(0)}%)`, esc(result.payment_type), usd(result.cc_surcharge_amount)] : null,
    ].filter(Boolean).map(([k, m, v]) => `<tr><td>${esc(k)}</td><td class="m">${esc(m)}</td><td class="r">${esc(v)}</td></tr>`).join("");
    const trip = (pickup.trim() || destination.trim())
      ? `<div class="sub"><b>From:</b> ${esc(pickup.trim() || "—")} &nbsp; <b>To:</b> ${esc(destination.trim() || "—")}</div>` : "";
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Buller Taxi — Special Rate</title>
<style>body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px}h1{font-size:18px;margin:0}
.sub{color:#666;font-size:12px;margin:2px 0 14px}table{width:100%;border-collapse:collapse;font-size:13px}
td{padding:6px 4px;border-bottom:1px solid #eee}td.r{text-align:right;font-weight:600}td.m{color:#666;font-size:12px}
.total{margin-top:14px;padding-top:10px;border-top:2px solid #111;display:flex;justify-content:space-between;font-size:18px;font-weight:800}
.meta{font-size:11px;color:#888;margin-top:16px}@media print{button{display:none}}</style></head><body>
<h1>Buller Taxi Services</h1><div class="sub">Weatherford Special Rate · ${esc(result.package_display)}</div>
${trip}
<table>${rows}</table>
<div class="total"><span>Total</span><span>${esc(usd(result.total))}</span></div>
${result.note ? `<div class="meta"><b>Note:</b> ${esc(result.note)}</div>` : ""}
<div class="meta">Pricing v${esc(result.pricing_version)} · effective ${esc(result.effective_date)}. City of Weatherford service area.</div>
</body></html>`;
    const w = window.open("", "specialRateSlip", "width=460,height=680");
    if (!w) { toast.error("Popup blocked — allow pop-ups to print"); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch { /* ignore */ } }, 250);
  };

  const maxRiders = pkg?.max_riders || 0;
  const suggestedKey = geo?.suggested_package_key || null;
  const weatherfordApplied = !!(geo?.both_inside && packageKey && packageKey === suggestedKey);
  const weatherfordOverridden = !!(geo?.both_inside && packageKey && suggestedKey && packageKey !== suggestedKey);

  return (
    <div
      data-testid="special-rate-calculator"
      className="mt-4 rounded-lg border border-amber-400/40 bg-gradient-to-br from-amber-950/20 via-zinc-950 to-zinc-950 p-4"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="grid place-items-center h-7 w-7 rounded-md bg-amber-400/20 border border-amber-400/50">
          <Sparkles className="h-4 w-4 text-amber-300" />
        </span>
        <div>
          <div className="text-sm font-black uppercase tracking-widest text-amber-100">Weatherford Special Rate Packages</div>
          <div className="text-[10px] text-zinc-400">City of Weatherford service area · locked per-rider + booking-fee packages</div>
        </div>
      </div>

      {/* Geo-fence address inputs (Google Places autocomplete) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1"><MapPin className="h-3 w-3" />Pickup address</Label>
          <div className="mt-1">
            <AddressAutocomplete
              testid="special-rate-pickup"
              value={pickup}
              onChange={(addr) => setPickup(addr)}
              placeholder="e.g. 100 W Tom Stafford Ave, Weatherford, OK"
            />
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1"><MapPin className="h-3 w-3" />Destination address</Label>
          <div className="mt-1">
            <AddressAutocomplete
              testid="special-rate-destination"
              value={destination}
              onChange={(addr) => setDestination(addr)}
              placeholder="e.g. SWOSU, Weatherford, OK"
            />
          </div>
        </div>
      </div>

      {/* Geo-fence status */}
      {checking && (
        <div data-testid="special-rate-geo-checking" className="mt-2 flex items-center gap-1.5 text-[11px] text-zinc-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking Weatherford city limits…
        </div>
      )}
      {!checking && weatherfordApplied && (
        <div data-testid="special-rate-weatherford-applied" className="mt-2 flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-300">
          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" /> Weatherford Rate Applied · {geo?.suggested_package_display}
          {geo?.miles != null && (
            <span data-testid="special-rate-miles" className="ml-1 inline-flex items-center gap-1 rounded bg-emerald-500/20 px-1.5 py-0.5 text-emerald-200">
              <Navigation className="h-3 w-3" />{geo.distance_source === "straight_line" ? "~" : ""}{geo.miles} mi
              {geo?.duration_minutes != null ? ` · ${geo.duration_minutes}m` : ""}
            </span>
          )}
        </div>
      )}
      {!checking && weatherfordOverridden && (
        <div data-testid="special-rate-weatherford-override" className="mt-2 flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-200">
          <Info className="h-3.5 w-3.5 flex-shrink-0" /> Weatherford detected (suggested {geo?.suggested_package_display}) · using your manual override
          {geo?.miles != null && (
            <span data-testid="special-rate-miles" className="ml-1 inline-flex items-center gap-1 rounded bg-amber-500/20 px-1.5 py-0.5 text-amber-100">
              <Navigation className="h-3 w-3" />{geo.distance_source === "straight_line" ? "~" : ""}{geo.miles} mi
            </span>
          )}
        </div>
      )}
      {!checking && geo && !geo.both_inside && (geo.pickup?.verified || geo.destination?.verified) && (
        <div data-testid="special-rate-geo-outside" className="mt-2 flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-[11px] text-zinc-400">
          <ShieldAlert className="h-3.5 w-3.5 flex-shrink-0" /> Not both inside Weatherford — no rate auto-applied. Select one manually if needed.
          {geo?.miles != null && (
            <span data-testid="special-rate-miles" className="ml-1 inline-flex items-center gap-1 rounded bg-zinc-800 px-1.5 py-0.5 text-zinc-300">
              <Navigation className="h-3 w-3" />{geo.distance_source === "straight_line" ? "~" : ""}{geo.miles} mi
            </span>
          )}
        </div>
      )}

      {/* Package picker */}
      <div className="mt-3">
        <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Rate category (special rate package)</Label>
        <Select value={packageKey} onValueChange={onPickPackage}>
          <SelectTrigger data-testid="special-rate-package" className="bg-zinc-950 border-zinc-700 h-9 mt-1">
            <SelectValue placeholder="Select a package…" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-80">
            {specials.map((s) => (
              <SelectItem key={s.key} value={s.key} data-testid={`special-rate-opt-${s.key}`}>
                {s.display} · {usd(s.per_rider)}/rider{Number(s.booking_fee) > 0 ? ` + ${usd(s.booking_fee)} booking` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {pkg && (
        <>
          {/* Locked package terms */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="special-rate-locked">
            <LockedChip label="Per-rider" value={usd(pkg.per_rider)} testid="special-rate-lock-perrider" />
            <LockedChip label="Booking fee" value={usd(pkg.booking_fee)} testid="special-rate-lock-booking" />
            <LockedChip label="Min riders" value={String(pkg.min_riders || 1)} testid="special-rate-lock-min" />
            <LockedChip label="Max riders" value={maxRiders ? String(maxRiders) : "No cap"} testid="special-rate-lock-max" />
          </div>
          {pkg.note && (
            <div data-testid="special-rate-note" className="mt-2 flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-200">
              <Info className="h-3.5 w-3.5 flex-shrink-0" />{pkg.note}
            </div>
          )}

          {/* Inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Riders (min {pkg.min_riders || 1}{maxRiders ? `, max ${maxRiders}` : ""})</Label>
              <Input
                data-testid="special-rate-riders"
                type="number" min={pkg.min_riders || 1} max={maxRiders || undefined} step="1"
                value={riders}
                onChange={(e) => setRiders(e.target.value)}
                className="bg-zinc-950 border-zinc-700 h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1"><Baby className="h-3 w-3" />Child safety seats ({usd(addons.child_seat_rate)})</Label>
              <Input
                data-testid="special-rate-childseats"
                type="number" min="0" step="1"
                value={childSeats}
                onChange={(e) => setChildSeats(e.target.value)}
                className="bg-zinc-950 border-zinc-700 h-9 mt-1"
              />
            </div>
            <div className="flex flex-col">
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1"><Wifi className="h-3 w-3" />Satcom Wi-Fi</Label>
              <label className="mt-1 flex items-center gap-2 bg-zinc-950 border border-zinc-700 rounded px-3 h-9 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="special-rate-satcom"
                  checked={satcom}
                  onChange={(e) => setSatcom(e.target.checked)}
                  className="h-4 w-4 accent-amber-400"
                />
                <span className="text-xs text-zinc-200">{usd(addons.satcom_wifi_rate)}/rider · free ≥ {usd(addons.satcom_free_threshold)}</span>
              </label>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1"><CreditCard className="h-3 w-3" />Payment type</Label>
              <Select value={paymentType} onValueChange={setPaymentType}>
                <SelectTrigger data-testid="special-rate-payment" className="bg-zinc-950 border-zinc-700 h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                  {PAYMENT_TYPES.map((p) => (
                    <SelectItem key={p.key} value={p.key} data-testid={`special-rate-pay-${p.key}`}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              type="button" data-testid="special-rate-calculate" onClick={() => runQuote(false)} disabled={loading}
              className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold uppercase tracking-wide"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Calculator className="h-4 w-4 mr-1.5" />}
              Calculate Special Rate
            </Button>
            <Button
              type="button" data-testid="special-rate-copy" onClick={copyBreakdown} disabled={!result}
              variant="outline" className="bg-zinc-950 border-zinc-700 h-9 text-xs"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" />Copy Breakdown
            </Button>
            <Button
              type="button" data-testid="special-rate-print" onClick={printQuote} disabled={!result}
              variant="outline" className="bg-zinc-950 border-zinc-700 h-9 text-xs"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" />Print
            </Button>
          </div>

          {result && (
            <div data-testid="special-rate-result" className="mt-4 rounded-lg border border-amber-400/30 bg-zinc-950/60 p-4">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <span className="text-[11px] uppercase tracking-widest text-amber-200 font-bold">{result.package_display}</span>
                <span className="text-right">
                  <span className="block text-[10px] uppercase tracking-wider text-zinc-500">Final total</span>
                  <span data-testid="special-rate-total" className="block text-2xl font-black text-amber-100">{usd(result.total)}</span>
                </span>
              </div>
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
                <Row label={`Per-rider × ${result.riders}`} value={usd(result.rider_subtotal)} />
                <Row label="Booking fee" value={usd(result.booking_fee)} />
                <Row label="Package fare" value={usd(result.package_fare)} />
                {result.child_seats > 0 && <Row label={`Child seats × ${result.child_seats}`} value={usd(result.child_seat_amount)} />}
                {result.satcom_wifi && <Row label="Satcom Wi-Fi" value={result.satcom_free ? "FREE" : usd(result.satcom_amount)} />}
                <Row label="Subtotal" value={usd(result.subtotal)} />
                {result.cc_surcharge_applies && <Row label={`Card surcharge (${(Number(result.cc_surcharge_pct) * 100).toFixed(0)}%)`} value={usd(result.cc_surcharge_amount)} />}
              </dl>
              {result.riders_capped && (
                <div className="text-[10px] text-amber-300 mt-2">Rider count capped at the package maximum ({result.max_riders}).</div>
              )}
              <div className="text-[10px] text-zinc-500 mt-3 pt-2 border-t border-zinc-800">
                Pricing v{result.pricing_version} · effective {result.effective_date} · no sales tax on rides
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function LockedChip({ label, value, testid }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 px-2.5 py-1.5" data-testid={testid}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-zinc-500">
        <Lock className="h-2.5 w-2.5" />{label}
      </div>
      <div className="text-sm font-bold text-amber-100 tabular-nums">{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</dt>
      <dd className="font-semibold text-zinc-100">{value}</dd>
    </div>
  );
}
